import { Info } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { ChronologyFilterBar } from '@/components/knowledge-graph/chronology-panel/ChronologyFilterBar';
import { ChronologyFilterSummary } from '@/components/knowledge-graph/chronology-panel/ChronologyFilterSummary';
import { ChronologyTimeline } from '@/components/knowledge-graph/chronology-panel/timeline';
import { useChronologyFilters } from '@/components/knowledge-graph/chronology-panel/useChronologyFilters';
import { OutputDisclaimer } from '@/components/shared/OutputDisclaimer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ChronologyInfo } from '@/hooks/useChronologies';
import type { ChronologyEventLight } from '@/hooks/useChronology';

export { getCategoryStyle, PALETTE } from '@/components/knowledge-graph/chronology-panel/styles';

interface ChronologyPanelProps {
  events: ChronologyEventLight[];
  onEventSelect?: (event: ChronologyEventLight | null) => void;
  selectedEventId?: string | null;
  matterGuid?: string;
  chronologyId?: string | null;
  description?: string;
  refreshStatus?: {
    label: string;
    tooltip: string;
    value: number;
  } | null;
  colorByCategory?: ReadonlyMap<string, string | null>;
  chronologies?: ChronologyInfo[];
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export function ChronologyPanel({
  events,
  onEventSelect,
  refreshStatus,
  colorByCategory,
  chronologies = [],
  selectedEventId,
  matterGuid,
  chronologyId = null,
  description,
  searchQuery: searchQueryProp,
  onSearchChange,
}: ChronologyPanelProps) {
  const { locale } = useMatterJurisdiction();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const isSelectionControlled = selectedEventId !== undefined;
  const effectiveSelectedId = isSelectionControlled ? selectedEventId : selectedId;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filters = useChronologyFilters({
    events,
    chronologies,
    searchQueryProp,
    onSearchChange,
  });
  const isAggregate = chronologies.length > 1 || (chronologies.length === 0 && filters.chronologyOptions.length > 1);
  const showSingleChronologyInfo = chronologies.length === 0 && filters.chronologyOptions.length === 1;

  const handleSelect = useCallback((event: ChronologyEventLight) => {
    const next = effectiveSelectedId === event.id ? null : event.id;
    if (!isSelectionControlled) setSelectedId(next);
    onEventSelect?.(next ? event : null);
  }, [effectiveSelectedId, isSelectionControlled, onEventSelect]);

  const showFilterBar = events.length > 0
    || filters.visibleCategoryGroups.length > 0
    || filters.visibleTypesByCategory.size > 0
    || filters.chronologyOptions.length > 1;
  const showTimelineHeader = events.length > 0 || Boolean(refreshStatus);
  const contentPaddingClassName = showTimelineHeader ? 'px-6 pt-5 pb-4' : 'px-6 py-4';

  return (
    <div className="flex h-full">
      <div
        ref={scrollContainerRef}
        className="subtle-scrollbar flex-1 overflow-y-scroll"
        style={{ overflowAnchor: 'none' }}
      >
        {description && (
          <div className="px-6 pt-4 text-xs text-slate-500">{description}</div>
        )}

        {showFilterBar && (
          <ChronologyFilterBar
            events={events}
            filteredEvents={filters.filteredEvents}
            matterGuid={matterGuid}
            chronologyId={chronologyId}
            locale={locale}
            colorByCategory={colorByCategory}
            isAggregate={isAggregate}
            showSingleChronologyInfo={showSingleChronologyInfo}
            chronologyOptions={filters.chronologyOptions}
            visibleCategoryGroups={filters.visibleCategoryGroups}
            visibleTypesByCategory={filters.visibleTypesByCategory}
            categoryCounts={filters.categoryCounts}
            typeCounts={filters.typeCounts}
            entityEventCounts={filters.entityEventCounts}
            creatorEventCounts={filters.creatorEventCounts}
            sourceEventCounts={filters.sourceEventCounts}
            eventYears={filters.eventYears}
            activeCategories={filters.activeCategories}
            excludedTypes={filters.excludedTypes}
            activeChronologies={filters.activeChronologies}
            activeEntities={filters.activeEntities}
            activeCreators={filters.activeCreators}
            activeSources={filters.activeSources}
            dateRange={filters.dateRange}
            relevanceThreshold={filters.relevanceThreshold}
            onToggleChronology={filters.toggleChronology}
            onToggleCategory={filters.toggleCategory}
            onToggleType={filters.togglePopoverType}
            onOnlyType={filters.showOnlyType}
            onToggleEntity={filters.toggleEntity}
            onToggleCreator={filters.toggleCreator}
            onToggleSource={filters.toggleSource}
            onShowAllTypesInCategory={filters.clearExcludedTypesForKeys}
            onClearEntities={filters.clearEntities}
            onClearCreators={filters.clearCreators}
            onClearSources={filters.clearSources}
            onSetDateRange={filters.setDateRange}
            onSetRelevanceThreshold={filters.setRelevanceThreshold}
          />
        )}

        {showTimelineHeader && (
          <div className="px-6 pt-4 flex items-start gap-4">
            <div className="min-w-0 flex-1">
              {events.length > 0 && (
                <ChronologyFilterSummary
                  eventsCount={events.length}
                  filteredEventsCount={filters.filteredEvents.length}
                  hasActiveFilters={filters.hasActiveFilters}
                  activeSources={filters.activeSources}
                  activeEntities={filters.activeEntities}
                  activeCreators={filters.activeCreators}
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  locale={locale}
                  onToggleSource={filters.toggleSource}
                  onToggleEntity={filters.toggleEntity}
                  onToggleCreator={filters.toggleCreator}
                  onClearDateRange={() => filters.setDateRange(undefined)}
                  onClearAllFilters={filters.clearFilters}
                />
              )}
              <OutputDisclaimer className={events.length > 0 ? 'mt-2 text-left' : 'mt-6 text-left'} />
            </div>
            {refreshStatus && (
              <div className="shrink-0 max-w-[18rem]">
                <div className="flex items-start justify-end gap-1.5">
                  <div className="min-w-0 pt-0.5 text-[11px] text-gray-500 text-right">
                    {refreshStatus.label}
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="mt-0.5 shrink-0 text-gray-400 transition-colors hover:text-gray-600"
                          aria-label="Projection progress information"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed">
                        {refreshStatus.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="ml-auto mt-2 h-1 w-32 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-indigo-500 transition-all duration-700 ease-out"
                    style={{ width: `${refreshStatus.value}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {!showTimelineHeader && (
          <div className="px-6 pt-4">
            <OutputDisclaimer className="text-left" />
          </div>
        )}

        {events.length > 0 ? (
          <div className={contentPaddingClassName}>
            {filters.filteredEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No events match the current filters.</p>
                <button type="button" onClick={filters.clearFilters} className="mt-1 text-sm font-medium text-gray-500 hover:text-gray-700">Clear filters</button>
              </div>
            ) : (
              <ChronologyTimeline
                datedEvents={filters.datedEvents}
                undatedEvents={filters.undatedEvents}
                selectedEventId={effectiveSelectedId}
                onSelectEvent={handleSelect}
                colorByCategory={colorByCategory}
                activeCategories={filters.activeCategories}
                excludedTypes={filters.excludedTypes}
                isAggregate={isAggregate}
                locale={locale}
                showUndated={filters.showUndated}
                onToggleUndated={() => filters.setShowUndated(value => !value)}
                onShowUndated={() => filters.setShowUndated(true)}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </div>
        ) : (
          <div className={contentPaddingClassName}>
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No events extracted yet.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
